import React, { useState, useEffect } from 'react';
import { Download, Calendar, BarChart3, TrendingUp, Clock, User, BookOpen, Filter } from 'lucide-react';
import axios from 'axios';

const ClassReport = () => {
  const [classes, setClasses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ 
    course_id: '', 
    semester_id: '', 
    department_id: '',
    day: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  useEffect(() => {
    fetchClasses();
    fetchCourses();
    fetchSemesters();
    fetchDepartments();
  }, []);

  const fetchClasses = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/routine/classes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClasses(response.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchSemesters = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/semesters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSemesters(response.data);
    } catch (error) {
      console.error('Error fetching semesters:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/departments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  // Get filtered classes
  const getFilteredClasses = () => {
    return classes.filter(cls => {
      if (filters.course_id && cls.course_id !== filters.course_id) return false;
      if (filters.semester_id && cls.semester_id !== filters.semester_id) return false;
      if (filters.department_id && cls.department_id !== filters.department_id) return false;
      if (filters.day && cls.day !== filters.day) return false;
      return true;
    });
  };

  const getCourseName = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course ? course.name : 'N/A';
  };

  const getSemesterName = (semId) => {
    const sem = semesters.find(s => s.id === semId);
    return sem ? sem.name : 'N/A';
  };

  const getDepartmentName = (deptId) => {
    const dept = departments.find(d => d.id === deptId);
    return dept ? dept.name : 'N/A';
  };

  // Group classes by day
  const groupClassesByDay = () => {
    const filteredClasses = getFilteredClasses();
    const grouped = {};
    
    daysOfWeek.forEach(day => {
      grouped[day] = filteredClasses.filter(cls => cls.day === day);
    });
    
    return grouped;
  };

  const handleExportReport = () => {
    const filteredClasses = getFilteredClasses();
    
    // Create CSV content
    const headers = ['Day', 'Time', 'Course', 'Semester', 'Department', 'Subject', 'Teacher', 'Status'];
    const rows = filteredClasses.map(cls => [
      cls.day,
      `${cls.start_time} - ${cls.end_time}`,
      getCourseName(cls.course_id),
      getSemesterName(cls.semester_id),
      getDepartmentName(cls.department_id),
      'Subject', // You can fetch subject name if needed
      'Teacher', // You can fetch teacher name if needed
      cls.is_active ? 'Active' : 'Inactive'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `class_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const filteredClasses = getFilteredClasses();
  const activeClasses = filteredClasses.filter(c => c.is_active);
  const inactiveClasses = filteredClasses.filter(c => !c.is_active);
  const groupedByDay = groupClassesByDay();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Class Schedule Report</h1>
          <p className="text-sm text-gray-500 mt-1">View and export class schedules</p>
        </div>
        <button 
          onClick={handleExportReport}
          className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
        >
          <Download className="h-4 w-4" />
          Export Report
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Classes</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{filteredClasses.length}</p>
            </div>
            <BarChart3 className="h-12 w-12 text-blue-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active</p>
              <p className="text-3xl font-bold text-green-600 mt-1">{activeClasses.length}</p>
            </div>
            <TrendingUp className="h-12 w-12 text-green-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Inactive</p>
              <p className="text-3xl font-bold text-red-600 mt-1">{inactiveClasses.length}</p>
            </div>
            <Calendar className="h-12 w-12 text-red-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Weekly Hours</p>
              <p className="text-3xl font-bold text-purple-600 mt-1">
                {filteredClasses.reduce((total, cls) => {
                  const start = cls.start_time.split(':');
                  const end = cls.end_time.split(':');
                  const hours = parseInt(end[0]) - parseInt(start[0]);
                  return total + hours;
                }, 0)}
              </p>
            </div>
            <Clock className="h-12 w-12 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-semibold">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Day</label>
            <select 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.day}
              onChange={(e) => setFilters({ ...filters, day: e.target.value })}
            >
              <option value="">All Days</option>
              {daysOfWeek.map(day => (
                <option key={day} value={day}>{day}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Course</label>
            <select 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.course_id}
              onChange={(e) => setFilters({ ...filters, course_id: e.target.value })}
            >
              <option value="">All Courses</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Semester</label>
            <select 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.semester_id}
              onChange={(e) => setFilters({ ...filters, semester_id: e.target.value })}
            >
              <option value="">All Semesters</option>
              {semesters.map(sem => (
                <option key={sem.id} value={sem.id}>{sem.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
            <select 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.department_id}
              onChange={(e) => setFilters({ ...filters, department_id: e.target.value })}
            >
              <option value="">All Departments</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button 
              onClick={() => setFilters({ course_id: '', semester_id: '', department_id: '', day: '' })}
              className="w-full bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>

      {/* Class Schedule Table - Grouped by Day */}
      {loading ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500">Loading class schedules...</p>
        </div>
      ) : filteredClasses.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <BarChart3 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No class schedules found</p>
          <p className="text-sm text-gray-400 mt-2">Add classes in Manage Classes to see them here</p>
        </div>
      ) : (
        <div className="space-y-6">
          {daysOfWeek.map(day => {
            const dayClasses = groupedByDay[day];
            if (dayClasses.length === 0) return null;

            return (
              <div key={day} className="bg-white rounded-lg shadow overflow-hidden">
                <div className="bg-blue-600 px-6 py-3">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    {day} ({dayClasses.length} classes)
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Semester</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {dayClasses
                        .sort((a, b) => a.start_time.localeCompare(b.start_time))
                        .map((cls) => (
                          <tr key={cls.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4 text-gray-400" />
                                <span className="text-sm font-medium text-gray-900">
                                  {cls.start_time} - {cls.end_time}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900">{getCourseName(cls.course_id)}</td>
                            <td className="px-6 py-4 text-sm text-gray-500">{getSemesterName(cls.semester_id)}</td>
                            <td className="px-6 py-4 text-sm text-gray-500">{getDepartmentName(cls.department_id)}</td>
                            <td className="px-6 py-4 text-center">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                cls.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {cls.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClassReport;
